<template>
  <div class="container-fluid">
    <div class="mt-4 row">
      <div class="col-12 d-flex justify-content-between">
        <h4 class="text-uppercase mb-0">Users</h4>
        <div class="form-group">
          <Datepicker v-model="selectedDate" dark auto-apply format="dd MMM yyyy" :max-date="new Date()" />
        </div>
      </div>
      <div class="col-12">
        <div class="card">
          <!-- Card header -->
          <div class="card-header" style="padding-bottom: 0rem;">
            <div class="d-flex justify-content-between">
              <div>
                <div v-if="selectedDate" class="pt-1 pb-4 text-lighter">
                  <i class="fa fa-clock"></i><span class="ms-2 font-weight-bold">
                    {{ formatDate(selectedDate) }}
                  </span>
                </div>
                <div>
                  <div class="mb-0 text-xs header-sm mb-1">DAY SUMMARY</div>
                  <h5 class="mb-0 text-uppercase">
                    {{ getPlatoonName(selectedPlatoon) + ' - ' + getSectionName(selectedSection) }}
                  </h5>
                  <div class="d-flex" style="height: 1.5rem">
                    <Transition>
                      <div v-if="selectedFilterTitle != ''" class="mt-1 text-lighter filter-title">{{
                        selectedFilterTitle }}</div>
                    </Transition>
                  </div>
                </div>
              </div>
              <div class="d-flex form-inline">
                <div class="form-group">
                  <PlatoonDropdown id="ddPlatoon" :allow-all="isAdminRole" :selected="selectedPlatoon"
                    @selected-platoon-id="
                      (selectedValue) => (selectedPlatoon = selectedValue)" />

                  <SectionDropdown id="ddSectionFilter" style="margin-left: 1rem;" :selected="selectedSection" @selected-section-id="
                    (selectedValue) => (selectedSection = selectedValue)
                  " />
                </div>
                <div class="form-group">
                  <argon-input id="search" name="search" :value="searchText" type="text" placeholder="Search..."
                    aria-label="Search" class="ms-3" @input="(event) => (searchText = event.target.value)"
                    @keyup.enter="search()" />
                </div>
              </div>
            </div>
          </div>
          <div class="card-header filter-card">
            <div id="filterSection" class="d-flex justify-content-start align-items-center filter-section">
              <div class="filter-chip-text text-lighter">Filter: </div>
              <div class="filter-chip">
                <el-popover effect="light" trigger="hover" placement="top" width="auto">
                  <template #default>
                    <div class="container">Battery level less than 20%</div>
                  </template>
                  <template #reference>
                    <div class="d-flex filter-chip-container align-items-center" :style="{
                      'background-color': getBgColor('LOW_BATTERY'),
                    }">
                      <div class="filter-chip-text" @click="filterTheTableByAlert('LOW_BATTERY')">Low Battery Level
                      </div>
                      <div class="reset-icon" v-show="'LOW_BATTERY' == this.selectedFilterType" @click="resetFilter()">
                        <icon-close />
                      </div>
                    </div>
                  </template>
                </el-popover>
              </div>
              <div class="filter-chip devider">|</div>
              <div class="filter-chip">
                <el-popover effect="light" trigger="hover" placement="top" width="auto">
                  <template #default>
                    <div class="container">Last synced device 7 to 13 days ago</div>
                  </template>
                  <template #reference>
                    <div class="d-flex filter-chip-container align-items-center" :style="{
                      'background-color': getBgColor('RECENT_INACTIVE'),
                    }">
                      <div class="filter-chip-text" @click="filterTheTableByAlert('RECENT_INACTIVE')">Recently Inactive
                      </div>
                      <div class="reset-icon" v-show="'RECENT_INACTIVE' == this.selectedFilterType"
                        @click="resetFilter()">
                        <icon-close />
                      </div>
                    </div>
                  </template>
                </el-popover>
              </div>
              <div class="filter-chip">
                <el-popover effect="light" trigger="hover" placement="top" width="auto">
                  <template #default>
                    <div class="container">Last synced device 14 to 29 days ago</div>
                  </template>
                  <template #reference>
                    <div class="d-flex filter-chip-container align-items-center" :style="{
                      'background-color': getBgColor('PROLONGED_INACTIVE'),
                    }">
                      <div class="filter-chip-text" @click="filterTheTableByAlert('PROLONGED_INACTIVE')">Prolonged
                        Inactivity</div>
                      <div class="reset-icon" v-show="'PROLONGED_INACTIVE' == this.selectedFilterType"
                        @click="resetFilter()">
                        <icon-close />
                      </div>
                    </div>
                  </template>
                </el-popover>
              </div>
              <div class="filter-chip">
                <el-popover effect="light" trigger="hover" placement="top" width="auto">
                  <template #default>
                    <div class="container">Last synced device more than 30 days ago</div>
                  </template>
                  <template #reference>
                    <div class="d-flex filter-chip-container align-items-center" :style="{
                      'background-color': getBgColor('EXTENDED_INACTIVE'),
                    }">
                      <div class="filter-chip-text" @click="filterTheTableByAlert('EXTENDED_INACTIVE')">Extended
                        Inactivity</div>
                      <div class="reset-icon" v-show="'EXTENDED_INACTIVE' == this.selectedFilterType"
                        @click="resetFilter()">
                        <icon-close />
                      </div>
                    </div>
                  </template>
                </el-popover>
              </div>
            </div>
          </div>
          <div class="card-body" style="padding-top: 0rem;">
            <div class="table-responsive">
              <el-table ref="dataTable" :data="users" style="width: 100%" @sort-change="sortColumn">
                <el-table-column prop="name" label="Name" sortable="custom" min-width="160">
                  <template #default="scope">
                    <span v-if="scope.row.user && scope.row.user.length > 0">
                      {{ scope.row.user[0].name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="platoon_name" label="Platoon" width="160" sortable="custom">
                  <template #default="scope">
                    <span :title="scope.row.user[0].platoon.platoon_name" class="truncate-text" v-if="scope.row.user && scope.row.user.length > 0">
                      {{ scope.row.user[0].platoon.platoon_name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="section_name" label="Section" width="160" sortable="custom">
                  <template #default="scope">
                    <span v-if="scope.row.user && scope.row.user.length > 0">
                      {{ scope.row.user[0].section.section_name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="strain" label="Strain" width="220" class-name="pe-2" sortable="custom">
                  <template #default="scope">
                    <argon-progress :percentage="scope.row.user_summary_daily.strain
                        ? (scope.row.user_summary_daily.strain * 100) / 21
                        : 0
                      " :left-text="`${scope.row.user_summary_daily.strain ? scope.row.user_summary_daily.strain : 0}`"
                      :left-sub-text="`${scope.row.user_summary_daily.previous_strain ? scope.row.user_summary_daily.previous_strain : 0}`" />
                  </template>
                </el-table-column>
                <el-table-column prop="recovery_score" label="Recovery" width="220" class-name="pe-2" sortable="custom">
                  <template #default="scope">
                    <argon-progress :color="getRecoveryScoreColor(
                      scope.row.user_summary_daily.recovery_score
                    )
                      " :percentage="scope.row.user_summary_daily.recovery_score
                          ? scope.row.user_summary_daily.recovery_score
                          : 0
                        "
                      :left-text="`${scope.row.user_summary_daily.recovery_score ? scope.row.user_summary_daily.recovery_score : 0}`"
                      :left-sub-text="`${scope.row.user_summary_daily.previous_recovery_score ? scope.row.user_summary_daily.previous_recovery_score : 0}`" />
                  </template>
                </el-table-column>
                <el-table-column prop="sleep_score" label="Sleep" width="220" class-name="pe-2" sortable="custom">
                  <template #default="scope">
                    <argon-progress :color="getSleepScoreColor(
                      scope.row.user_summary_daily.sleep_score
                    )
                      " :percentage="scope.row.user_summary_daily.sleep_score
                          ? scope.row.user_summary_daily.sleep_score
                          : 0
                        "
                      :left-text="`${scope.row.user_summary_daily.sleep_score ? scope.row.user_summary_daily.sleep_score : 0}`"
                      :left-sub-text="`${scope.row.user_summary_daily.previous_sleep_score ? scope.row.user_summary_daily.previous_sleep_score : 0}`"
                      :right-text="parseMinutesToSleepHours(
                        scope.row.user_summary_daily.sleep_minutes
                      )
                        " />
                  </template>
                </el-table-column>
                <el-table-column prop="workouts" label="Workouts" min-width="120">
                  <template #default="scope">
                    <div v-if="scope.row.user_summary_daily.workouts" class="d-flex">
                      <div v-for="(workout, idx) in scope.row.user_summary_daily.workouts"
                        :key="workout.workout_record_id">
                        <el-popover effect="light" trigger="hover" placement="top" width="auto">
                          <template #default>
                            <div class="container">
                              <div class="header">
                                <div class="title">{{ getWorkoutName(workout.workout_type) }}</div>
                              </div>
                              <div class="time-range">{{ formatUnixTimeStamp(workout.start_time) }} - {{
                                formatUnixTimeStamp(workout.end_time) }}</div>
                              <div class="stats">
                                <div class="stat">
                                  <icon-duration></icon-duration>
                                  <div class="details">{{ formatDurationInSec(workout.total_time) }}</div>
                                </div>
                                <div class="stat">
                                  <icon-calories></icon-calories>
                                  <div class="details">{{ workout.calories }} kcal</div>
                                </div>
                                <div class="stat" v-if="distanceWorkoutTypes.includes(workout.workout_type)">
                                  <icon-location></icon-location>
                                  <div class="details">{{ workout.workout_distance / 1000 }} KM</div>
                                </div>
                                <div class="stat">
                                  <icon-vitals></icon-vitals>
                                  <div class="details">{{ workout.min_hr }} - {{ workout.max_hr }} bpm</div>
                                </div>
                              </div>
                            </div>
                          </template>
                          <template #reference>
                            <img v-if="idx < maxWorkouts" :src="getWorkoutIcon(workout.workout_type)"
                              onerror="this.onerror=null; this.src='/img/icons/ic_workout_255.svg'"
                              :alt="getWorkoutName(workout.workout_type)" class="me-2" />
                          </template>
                        </el-popover>
                      </div>
                      <span v-if="
                        scope.row.workouts &&
                        scope.row.workouts.length > maxWorkouts
                      ">
                        +{{ scope.row.workouts.length - maxWorkouts }}
                      </span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="battery" label="Device Name" min-width="145">
                  <template #default="scope">
                    <div v-if="scope.row.user &&
                      scope.row.user.length > 0 && scope.row.user[0].device_info">
                      <span>{{ 'Band ' + scope.row.user[0].device_info.split(" ")[scope.row.user[0].device_info.split(" ").length - 1] }}</span>
                    </div>
                    <span v-else>-</span>
                  </template>
                </el-table-column>

                <el-table-column prop="battery" label="Device Battery" sortable="custom" min-width="145">
                  <template #default="scope">
                    <div v-if="
                      scope.row.user &&
                      scope.row.user.length > 0 &&
                      scope.row.user[0].battery
                    ">
                      <img :src="getBatteryIcon(scope.row.user[0].battery)" alt="Battery level" class="me-2" />
                      <span :class="{
                        'text-danger':
                          scope.row.user[0].battery <= lowBatteryLevel,
                      }">{{ scope.row.user[0].battery }}%</span>
                    </div>
                    <span v-else>-</span>
                  </template>
                </el-table-column>

                <el-table-column prop="last_sync" label="Last Sync" sortable="custom" min-width="180">
                  <template #default="scope">
                    <div v-if="scope.row.user && scope.row.user.length > 0">
                      {{ formatUnixDateTime(scope.row.user[0].last_sync) }}
                    </div>
                  </template>
                </el-table-column>

                <el-table-column prop="last_updated" label="Device Last Connected" sortable="custom" min-width="200">
                  <template #default="scope">
                    <div v-if="scope.row.user && scope.row.user.length > 0">
                      {{ formatDateTime(scope.row.user[0].last_updated) }}
                    </div>
                  </template>
                </el-table-column>

                <el-table-column prop="app_version" label="App Version" sortable="custom" min-width="140">
                  <template #default="scope">
                    <div v-if="scope.row.user && scope.row.user.length > 0">
                      {{ scope.row.user[0].app_version }}
                    </div>
                  </template>
                </el-table-column>

                <el-table-column label="" width="60">
                  <template #default="scope">
                    <div class="d-flex">
                      <router-link target="_blank" :to="{
                        name: `User`,
                        params: { udid: scope.row.udid },
                        query: { date: getDate() },
                      }" exact>
                        <icon-user-info />
                      </router-link>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
          <div class="card-footer">
            <div class="d-flex justify-content-end align-items-center">
              <app-pagination :records="count" :page-size="pageSize" :page="page" @update-page-selection="
                (selectedValue) => updatePageSelection(selectedValue)
              " />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Constants from "@/helpers/constants";
import Utils from "@/helpers/utils";
import DateUtils from "@/helpers/dateUtils";

import ArgonInput from "@/shared/Controls/ArgonInput.vue";
import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import IconUserInfo from "@/shared/Controls/Icons/IconUserInfo.vue";
import IconCalories from "@/shared/Controls/Icons/IconCalories.vue";
import IconDuration from "@/shared/Controls/Icons/IconDuration.vue";
import IconLocation from "@/shared/Controls/Icons/IconLocation.vue";
import IconVitals from "@/shared/Controls/Icons/IconVitals.vue";
import IconClose from "@/shared/Controls/Icons/IconClose.vue";
import Datepicker from "@vuepic/vue-datepicker";
import { Tooltip } from "bootstrap/dist/js/bootstrap.esm.min.js";

import SectionDropdown from "../components/SectionDropdown.vue";
import PlatoonDropdown from "../components/PlatoonDropdown.vue";
import dayjs from "dayjs";
const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

export default {
  name: "UsersTable",
  components: {
    ArgonInput,
    ArgonProgress,
    IconUserInfo,
    SectionDropdown,
    Datepicker,
    IconCalories,
    IconDuration,
    IconLocation,
    IconVitals,
    IconClose,
    PlatoonDropdown,
  },
  data() {
    return {
      workoutTypes: [],
      maxWorkouts: Constants.MAX_WORKOUTS,
      selectedDate: dayjs().utc().startOf("day"),
      selectedSection: -1, // filter
      page: Constants.DEFAULT_PAGE,
      pageSize: Constants.DEFAULT_PAGE_SIZE,
      sort: "last_sync",
      order: "desc",
      count: 0,
      lowBatteryLevel: Constants.BATTERY_LOW_LEVEL,
      searchText: "",
      users: [],
      distanceWorkoutTypes: Constants.DISTANCE_WORKOUT_TYPES,
      selectedFilterTitle: '',
      selectedFilterType: '',
      battery: 0,
      sync_from: 0,
      sync_to: 0,
      selectedPlatoon: -1, // filter
      isAdminRole: false,
    };
  },
  watch: {
    selectedDate() {
      // for filter
      this.page = 1;
      if (this.selectedDate instanceof Date) {
        this.getUsersToday();
      } else this.selectedDate = new Date();
    },
    selectedSection() {
      // for filter
      this.page = 1;
      this.getUsersToday();
    },
    selectedPlatoon() {
      // for filter
      this.page = 1;
      this.getUsersToday();
    },
    searchText() {
      if (this.searchText.length == 0) {
        this.page = 1;
        this.getUsersToday();
      }
    },
  },
  mounted() {
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });
    let loggedInUser = this.$store.getters["auth/getLoggedInUser"]();
    if (loggedInUser.role && loggedInUser.role.role_id == Constants.ROLES.PLATOON_COMMANDER) {
      this.selectedPlatoon = loggedInUser.platoon_id;
    }
    if (Constants.ADMIN_USER_ROLES.includes(loggedInUser.role.role_id)) {
      this.isAdminRole = true;
    }
    this.getWorkoutTypes();
    this.getUsersToday();
  },
  methods: {
    getWorkoutTypes() {
      this.$store
        .dispatch("list/getWorkoutTypes", {
          sort: "workout_type_id",
          order: "asc",
          is_active: -1,
        })
        .then(({ data }) => {
          if (data && data.length > 0) {
            this.workoutTypes = data;
          }
        });
    },
    getUsersToday() {
      this.$store
        .dispatch("data/today", {
          page: this.page,
          page_size: this.pageSize,
          sort: this.sort,
          order: this.order,
          section_id: this.selectedSection || -1,
          search_term: this.searchText,
          dt_at: dayjs(this.selectedDate).utc().format(Constants.BUCKET_FORMAT),
          battery: this.battery,
          sync_from: this.sync_from,
          sync_to: this.sync_to,
          platoon_id: this.selectedPlatoon || -1,
        })
        .then((data) => {
          const { count, rows } = data.data;
          this.count = count;
          this.users = rows;
        });
    },
    compareUnixDateAndReturn(unixTs, fromDate, toDate) {
      let dateTime = Utils.formatToBucketFormat(unixTs);
      return dateTime >= fromDate && dateTime <= toDate;
    },
    getWorkoutName(workoutType) {
      const workoutTypeName =
        this.$store.getters["list/getWorkoutTypeName"](workoutType);

      return workoutTypeName;
    },
    sortColumn({ prop, order }) {
      this.sort = prop;
      this.order = order == "ascending" ? "asc" : "desc";
      this.getUsersToday();
    },
    updatePageSelection(newPageIndex) {
      this.page = newPageIndex;
      this.getUsersToday();
    },
    search() {
      this.page = 1;
      this.getUsersToday();
    },
    getSectionName(section_id) {
      const section = this.$store.getters["list/getSectionName"](section_id);
      return section ? section.name : "";
    },
    getPlatoonName(platoon_id) {
      const platoon = this.$store.getters["list/getPlatoonName"](platoon_id);
      return platoon ? platoon.name : "";
    },
    getSleepScoreColor(score) {
      return Utils.getSleepScoreColor(score);
    },
    getRecoveryScoreColor(score) {
      return Utils.getRecoveryScoreColor(score);
    },
    getWorkoutIcon(workoutType) {
      return Utils.getWorkoutIcon(workoutType);
    },
    getBatteryIcon(batteryLevel) {
      switch (true) {
        case batteryLevel <= Constants.BATTERY_LEVEL_VERY_LOW:
          return `/img/icons/ic_battery_very_low.svg`;
        case batteryLevel <= Constants.BATTERY_LEVEL_LOW:
          return `/img/icons/ic_battery_low.svg`;
        case batteryLevel <= Constants.BATTERY_LEVEL_AVERAGE:
          return `/img/icons/ic_battery_average.svg`;
        case batteryLevel <= Constants.BATTERY_LEVEL_HIGH:
          return `/img/icons/ic_battery_high.svg`;
        case batteryLevel <= Constants.BATTERY_LEVEL_MAX:
          return `/img/icons/ic_battery_max.svg`;
        default:
          return `/img/icons/ic_battery_average.svg`;
      }
    },
    formatDate(selectedDate) {
      if (selectedDate) {
        return dayjs(selectedDate).format("ddd, DD MMM YYYY");
      }
      return "";
    },
    formatDateTime(lastSyncTs) {
      return DateUtils.formatDateToProvidedDate(lastSyncTs, Constants.TABLE_DATETIME_YEAR_FORMAT);
    },
    formatUnixDateTime(lastSyncTs) {
      return Utils.formatDateTimeStamp(lastSyncTs);
    },
    parseMinutesToSleepHours(minutes) {
      return Utils.parseMinutesToSleepHours(minutes);
    },
    getDate() {
      return DateUtils.formatDateToBucketDate(this.selectedDate)
    },
    formatUnixTimeStamp(unixTS) {
      let timeVal = dayjs(Utils.formatTimestamp(unixTS)).format(Constants.MDY_TS_FORMAT);
      return dayjs(timeVal).format(Constants.TIME_12_HRS_FORMAT);
    },
    formatDurationInSec(durationInSec) {
      return Utils.formatDurationInSec(durationInSec);
    },
    filterTheTableByAlert(filterType) {
      if (filterType == this.selectedFilterType) {
        return;
      }
      this.battery = 0;
      this.sync_from = 0;
      this.sync_to = 0;
      this.selectedFilterType = filterType;
      this.selectedFilterTitle = this.getFilterTitle(filterType);
      if (Constants.FILTER_LOW_BATTERY == filterType) {
        this.battery = 20;
      } else if (Constants.FILTER_RECENT_INACTIVE == filterType) {
        let epochDates = this.getEpochDates(13, 7);
        this.sync_from = epochDates.fromDate;
        this.sync_to = epochDates.toDate;
      } else if (Constants.FILTER_PROLONGED_INACTIVE == filterType) {
        let epochDates = this.getEpochDates(29, 14);
        this.sync_from = epochDates.fromDate;
        this.sync_to = epochDates.toDate;
      } else if (Constants.FILTER_EXTENDED_INACTIVE == filterType) {
        let epochDates = this.getEpochDates(30, 30);
        this.sync_from = epochDates.fromDate;
        this.sync_to = 0;
      }

      this.getUsersToday();
    },
    getEpochDates(fromDays, toDays) {
      let fromDate = DateUtils.getBeforeNthDayFromToday(new Date(), fromDays);
      let toDate = DateUtils.getBeforeNthDayFromToday(new Date(), toDays);
      let epochDates = {
        fromDate: new Date(fromDate).getTime(),
        toDate: new Date(toDate).getTime(),
      };

      return epochDates;
    },
    resetFilter() {
      this.battery = 0;
      this.sync_from = 0;
      this.sync_to = 0;
      this.selectedFilterTitle = '';
      this.selectedFilterType = '';
      this.getUsersToday();
    },
    getBgColor(filterType) {
      if (filterType == this.selectedFilterType) {
        return '#359685';
      }

      return '#262e30';
    },
    getFilterTitle(filterType) {
      if (filterType == Constants.FILTER_LOW_BATTERY) {
        return Constants.FILTER_LOW_BATTERY_TITLE;
      }

      if (filterType == Constants.FILTER_RECENT_INACTIVE) {
        return Constants.FILTER_RECENT_INACTIVE_TITLE;
      }

      if (filterType == Constants.FILTER_PROLONGED_INACTIVE) {
        return Constants.FILTER_PROLONGED_INACTIVE_TITLE;
      }

      if (filterType == Constants.FILTER_EXTENDED_INACTIVE) {
        return Constants.FILTER_EXTENDED_INACTIVE_TITLE;
      }

      return '';
    },
  },
};
</script>
<style>
.container {
  background: transparent;
  color: white;
  padding: 0.6rem;
  width: 300px;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.time-range {
  font-size: 0.8rem;
  color: #A0A0A0;
  margin-bottom: 1rem;
}

.stats {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat {
  display: flex;
  align-items: center;
  justify-content: left;
  margin-bottom: 0.25rem;
}

.icon {
  font-size: 1rem;
}

.details {
  font-size: 0.9rem;
  padding-left: 0.5rem;
}

.el-popper.is-light {
  background: #040505 !important;
  border-radius: 0.5rem !important;
  padding: 0rem !important;
  margin: 0rem !important;
}

.filter-card {
  padding-top: 0.5rem;
  padding-bottom: 0rem;
  margin-bottom: 0.5rem;
}

.filter-section {
  background: #262e30;
  padding: 1rem;
  border-radius: 0.5rem;
}

.filter-chip {
  cursor: pointer;
  border-radius: 0.5rem;
  width: fit-content;
  padding-left: 0.75rem;
  padding-right: 0.25rem;
  font-size: 0.85rem;
  color: white;
  text-decoration: none;
}

.filter-chip-text {
  font-size: 0.85rem;
}

.filter-chip-container {
  width: fit-content;
  cursor: pointer;
  border-radius: 1rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  padding-left: 0.75rem;
  padding-right: 0.5rem;
  font-size: 0.9rem !important;
}

.filter-title {
  font-size: 0.85rem !important;
}

.reset-filter {
  font-size: 1.1rem;
  cursor: pointer;
  color: orange;
  padding-left: 1rem;
}

.devider {
  color: #656060;
  font-size: 0.85rem;
}
.truncate-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
